import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { theme } from '../../../theme';
import { apiRequest } from '../../../utils/apiUtils';
import { VenueType } from '../../../types/venue';
import {
  ChevronLeft,
  Check,
  Upload,
  X,
  Save,
  Plus,
} from 'lucide-react';

const VENUE_TYPES: { value: VenueType; label: string }[] = [
  { value: 'FUTBOL_5', label: 'Fútbol 5' },
  { value: 'FUTBOL_7', label: 'Fútbol 7' },
  { value: 'FUTBOL_11', label: 'Fútbol 11' },
];

const SURFACE_TYPES = [
  { value: 'ARTIFICIAL_GRASS', label: 'Césped Sintético' },
  { value: 'NATURAL_GRASS', label: 'Césped Natural' },
  { value: 'CONCRETE', label: 'Cemento' },
  { value: 'PARQUET', label: 'Parquet' },
];

interface Complex {
  id: string;
  name: string;
  address: string;
  city: string;
  province: string;
}

interface FormData {
  name: string;
  venueType: VenueType;
  surfaceType: string;
  capacity: number;
  width: number;
  length: number;
  hourlyRate: number;
  hasLighting: boolean;
  isIndoor: boolean;
  hasGoals: boolean;
  hasNets: boolean;
}

const AddFieldPage: React.FC = () => {
  const navigate = useNavigate();
  const { complexId: paramComplexId } = useParams<{ complexId: string }>();
  const [searchParams] = useSearchParams();
  const complexIdFromQuery = searchParams.get('complex_id');
  const complexId = paramComplexId || complexIdFromQuery;

  const [complex, setComplex] = useState<Complex | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadingComplex, setLoadingComplex] = useState(true);
  const [formData, setFormData] = useState<FormData>({
    name: '',
    venueType: 'FUTBOL_5',
    surfaceType: 'ARTIFICIAL_GRASS',
    capacity: 10,
    width: 25,
    length: 40,
    hourlyRate: 15000,
    hasLighting: true,
    isIndoor: false,
    hasGoals: true,
    hasNets: false,
  });

  useEffect(() => {
    if (complexId) {
      fetchComplexDetail();
    } else {
      setLoadingComplex(false);
    }
  }, [complexId]);

  const fetchComplexDetail = async () => {
    try {
      setLoadingComplex(true);
      const response = await apiRequest(`/complexes/${complexId}`);
      if (response.data?.complex) {
        setComplex(response.data.complex);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error fetching complex:', error);
      alert('Error al cargar el complejo deportivo');
    } finally {
      setLoadingComplex(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent, addAnother = false) => {
    e.preventDefault();

    if (!complex) {
      alert('Debe seleccionar un complejo deportivo');
      return;
    }

    if (!formData.name.trim()) {
      alert('El nombre de la cancha es requerido');
      return;
    }

    try {
      setLoading(true);

      const venueData = {
        name: formData.name.trim(),
        complexId: complex.id,
        venueType: formData.venueType,
        surfaceType: formData.surfaceType,
        capacity: formData.capacity,
        width: formData.width,
        length: formData.length,
        hourlyRate: formData.hourlyRate,
        hasLighting: formData.hasLighting,
        isIndoor: formData.isIndoor,
        hasGoals: formData.hasGoals,
        hasNets: formData.hasNets,
        // Heredar datos del complejo
        address: complex.address,
        city: complex.city,
        province: complex.province,
        // Valores por defecto
        description: `Cancha ${formData.venueType.replace('_', ' ')} en ${complex.name}`,
        mapsUrl: '',
        hasParking: false,
        hasChangingRooms: false,
        hasShower: false,
        isActive: true,
      };

      const response = await apiRequest('/venues', {
        method: 'POST',
        body: venueData,
      });

      if (response.data?.venue) {
        if (addAnother) {
          // Resetear formulario
          setFormData({
            name: '',
            venueType: 'FUTBOL_5',
            surfaceType: 'ARTIFICIAL_GRASS',
            capacity: 10,
            width: 25,
            length: 40,
            hourlyRate: 15000,
            hasLighting: true,
            isIndoor: false,
            hasGoals: true,
            hasNets: false,
          });
          alert('Cancha creada exitosamente. Podés agregar otra.');
        } else {
          alert('Cancha creada exitosamente');
          navigate(`/owner/complexes/${complex.id}`);
        }
      }
    } catch (error: any) {
      // eslint-disable-next-line no-console
      console.error('Error creating venue:', error);
      alert(error.message || 'Error al crear la cancha');
    } finally {
      setLoading(false);
    }
  };

  const updateField = <K extends keyof FormData>(field: K, value: FormData[K]) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const toggleAmenity = (amenity: keyof FormData) => {
    setFormData(prev => ({ ...prev, [amenity]: !prev[amenity] as any }));
  };

  const loadingContainerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#181A1B',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '20px',
  };

  const loadingTextStyle: React.CSSProperties = {
    fontSize: '18px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#B0BEC5',
  };

  const errorTextStyle: React.CSSProperties = {
    fontSize: '18px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FF5252',
  };

  const errorButtonStyle: React.CSSProperties = {
    backgroundColor: theme.colors.primary,
    color: '#000',
    border: 'none',
    borderRadius: '12px',
    padding: '12px 24px',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    fontWeight: 600,
    cursor: 'pointer',
  };

  if (loadingComplex) {
    return (
      <div style={loadingContainerStyle}>
        <div style={loadingTextStyle}>Cargando complejo...</div>
      </div>
    );
  }

  if (!complex) {
    return (
      <div style={loadingContainerStyle}>
        <div style={errorTextStyle}>Complejo deportivo no encontrado</div>
        <button
          onClick={() => navigate('/owner/venues')}
          style={errorButtonStyle}
        >
          Volver a complejos
        </button>
      </div>
    );
  }

  // Styles
  const containerStyle: React.CSSProperties = {
    minHeight: '100vh',
    backgroundColor: '#181A1B',
    display: 'flex',
    flexDirection: 'column',
  };

  const headerStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    borderBottom: '1px solid #33383B',
    padding: '32px 48px',
  };

  const backButtonStyle: React.CSSProperties = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: '8px',
    background: 'none',
    border: 'none',
    color: '#00E676',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    cursor: 'pointer',
    padding: 0,
    marginBottom: '12px',
  };

  const titleStyle: React.CSSProperties = {
    fontSize: '36px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#00E676',
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
    marginBottom: '12px',
  };

  const subtitleStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#B0BEC5',
    fontWeight: 'normal',
    margin: 0,
  };

  const mainContentStyle: React.CSSProperties = {
    display: 'flex',
    gap: '32px',
    padding: '32px 48px',
    flex: 1,
  };

  const formColumnStyle: React.CSSProperties = {
    width: '700px',
    display: 'flex',
    flexDirection: 'column',
    gap: '28px',
  };

  const rightColumnStyle: React.CSSProperties = {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const sectionStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const sectionTitleStyle: React.CSSProperties = {
    fontSize: '20px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFFFFF',
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
  };

  const fieldStyle: React.CSSProperties = {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
  };

  const labelStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFFFFF',
    fontWeight: '600',
  };

  const inputStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    border: '1px solid #33383B',
    borderRadius: '10px',
    height: '48px',
    padding: '12px 16px',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFFFFF',
    width: '100%',
  };

  const selectStyle: React.CSSProperties = {
    ...inputStyle,
    appearance: 'none',
    backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='20' height='20' viewBox='0 0 24 24' fill='none' stroke='%23B0BEC5' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpolyline points='6 9 12 15 18 9'%3E%3C/polyline%3E%3C/svg%3E")`,
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'right 16px center',
    paddingRight: '48px',
  };

  const rowStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const amenityStyle = (isActive: boolean): React.CSSProperties => ({
    backgroundColor: '#23272A',
    border: isActive ? '2px solid #00E676' : '1px solid #33383B',
    borderRadius: '10px',
    height: '48px',
    padding: '12px 16px',
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    cursor: 'pointer',
    flex: 1,
  });

  const amenityTextStyle = (isActive: boolean): React.CSSProperties => ({
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: isActive ? '#FFFFFF' : '#B0BEC5',
    fontWeight: isActive ? '600' : 'normal',
  });

  const infoCardStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    border: '1px solid #FFC107',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '12px',
  };

  const cardTitleStyle: React.CSSProperties = {
    fontSize: '16px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFC107',
    fontWeight: 'normal',
    letterSpacing: '0.5px',
    margin: 0,
  };

  const cardTextStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#B0BEC5',
    fontWeight: 'normal',
    margin: 0,
    lineHeight: '1.6',
  };

  const statusCardStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    border: '1px solid #33383B',
    borderRadius: '12px',
    padding: '20px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  };

  const statusRowStyle: React.CSSProperties = {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const statusLabelStyle: React.CSSProperties = {
    fontSize: '13px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#B0BEC5',
    fontWeight: 'normal',
  };

  const statusValueStyle: React.CSSProperties = {
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFFFFF',
    fontWeight: '600',
  };

  const actionBarStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    borderTop: '1px solid #33383B',
    padding: '24px 48px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  };

  const buttonGroupStyle: React.CSSProperties = {
    display: 'flex',
    gap: '16px',
  };

  const cancelButtonStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    border: '1px solid #33383B',
    borderRadius: '12px',
    height: '52px',
    width: '160px',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#FFFFFF',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const saveAddButtonStyle: React.CSSProperties = {
    backgroundColor: '#23272A',
    border: '1px solid #00E676',
    borderRadius: '12px',
    height: '52px',
    width: '200px',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#00E676',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  const saveButtonStyle: React.CSSProperties = {
    backgroundColor: '#00E676',
    border: 'none',
    borderRadius: '12px',
    height: '52px',
    width: '220px',
    fontSize: '14px',
    fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif",
    color: '#000000',
    fontWeight: '600',
    cursor: 'pointer',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  };

  return (
    <div style={containerStyle}>
      {/* Header */}
      <div style={headerStyle}>
        <button
          onClick={() => navigate(`/owner/complexes/${complex.id}`)}
          style={backButtonStyle}
        >
          <ChevronLeft size={20} />
          Volver al complejo
        </button>
        <h1 style={titleStyle}>AGREGAR CANCHA</h1>
        <p style={subtitleStyle}>
          {complex.name} - Agrega las canchas de tu complejo con sus características específicas
        </p>
      </div>

      {/* Main Content */}
      <div style={mainContentStyle}>
        {/* Form Column */}
        <div style={formColumnStyle}>
          {/* Basic Info Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>INFORMACIÓN BÁSICA</h2>

            <div style={fieldStyle}>
              <label style={labelStyle}>Nombre de la Cancha</label>
              <input
                type="text"
                placeholder="Ej: Cancha Principal"
                value={formData.name}
                onChange={(e) => updateField('name', e.target.value)}
                style={inputStyle}
              />
            </div>

            <div style={rowStyle}>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Tipo de Fútbol</label>
                <select
                  value={formData.venueType}
                  onChange={(e) => updateField('venueType', e.target.value as VenueType)}
                  style={selectStyle}
                >
                  {VENUE_TYPES.map(type => (
                    <option key={type.value} value={type.value}>
                      {type.label}
                    </option>
                  ))}
                </select>
              </div>

              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Superficie</label>
                <select
                  value={formData.surfaceType}
                  onChange={(e) => updateField('surfaceType', e.target.value)}
                  style={selectStyle}
                >
                  {SURFACE_TYPES.map(surface => (
                    <option key={surface.value} value={surface.value}>
                      {surface.label}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Capacidad de Jugadores</label>
              <input
                type="number"
                placeholder="Ej: 10 jugadores"
                value={formData.capacity}
                onChange={(e) => updateField('capacity', parseInt(e.target.value) || 0)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Specifications Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>ESPECIFICACIONES</h2>

            <div style={rowStyle}>
              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Ancho (metros)</label>
                <input
                  type="number"
                  placeholder="Ej: 25"
                  value={formData.width}
                  onChange={(e) => updateField('width', parseFloat(e.target.value) || 0)}
                  style={inputStyle}
                />
              </div>

              <div style={{ ...fieldStyle, flex: 1 }}>
                <label style={labelStyle}>Largo (metros)</label>
                <input
                  type="number"
                  placeholder="Ej: 40"
                  value={formData.length}
                  onChange={(e) => updateField('length', parseFloat(e.target.value) || 0)}
                  style={inputStyle}
                />
              </div>
            </div>

            <div style={fieldStyle}>
              <label style={labelStyle}>Precio por Hora (ARS)</label>
              <input
                type="number"
                placeholder="Ej: 15000"
                value={formData.hourlyRate}
                onChange={(e) => updateField('hourlyRate', parseFloat(e.target.value) || 0)}
                style={inputStyle}
              />
            </div>
          </div>

          {/* Amenities Section */}
          <div style={sectionStyle}>
            <h2 style={sectionTitleStyle}>CARACTERÍSTICAS DE LA CANCHA</h2>

            <div style={rowStyle}>
              <div
                onClick={() => toggleAmenity('hasLighting')}
                style={amenityStyle(formData.hasLighting)}
              >
                {formData.hasLighting && <Check size={20} color="#00E676" />}
                <span style={amenityTextStyle(formData.hasLighting)}>Iluminación</span>
              </div>

              <div
                onClick={() => toggleAmenity('isIndoor')}
                style={amenityStyle(formData.isIndoor)}
              >
                {formData.isIndoor && <Check size={20} color="#00E676" />}
                <span style={amenityTextStyle(formData.isIndoor)}>Techada</span>
              </div>
            </div>

            <div style={rowStyle}>
              <div
                onClick={() => toggleAmenity('hasGoals')}
                style={amenityStyle(formData.hasGoals)}
              >
                {formData.hasGoals && <Check size={20} color="#00E676" />}
                <span style={amenityTextStyle(formData.hasGoals)}>Arcos Reglamentarios</span>
              </div>

              <div
                onClick={() => toggleAmenity('hasNets')}
                style={amenityStyle(formData.hasNets)}
              >
                {formData.hasNets && <Check size={20} color="#00E676" />}
                <span style={amenityTextStyle(formData.hasNets)}>Redes Nuevas</span>
              </div>
            </div>
          </div>
        </div>

        {/* Right Column */}
        <div style={rightColumnStyle}>
          {/* Status Card */}
          <div style={statusCardStyle}>
            <h3 style={{ ...sectionTitleStyle, fontSize: '16px' }}>ESTADO DE LA CANCHA</h3>
            <div style={statusRowStyle}>
              <span style={statusLabelStyle}>Complejo</span>
              <span style={statusValueStyle}>{complex.name}</span>
            </div>
            <div style={statusRowStyle}>
              <span style={statusLabelStyle}>Ubicación</span>
              <span style={statusValueStyle}>{complex.city}</span>
            </div>
            <div style={statusRowStyle}>
              <span style={statusLabelStyle}>Estado</span>
              <span style={{ ...statusValueStyle, color: '#00E676' }}>Nueva</span>
            </div>
          </div>

          {/* Info Card */}
          <div style={infoCardStyle}>
            <h3 style={cardTitleStyle}>💡 CONSEJO</h3>
            <p style={cardTextStyle}>
              Asegurate de ingresar el precio correcto por hora y las características exactas
              de la cancha para que los jugadores puedan reservarla con confianza.
            </p>
            <p style={cardTextStyle}>
              Podés agregar fotos después de crear la cancha desde la sección de edición.
            </p>
          </div>
        </div>
      </div>

      {/* Action Bar */}
      <div style={actionBarStyle}>
        <div>
          <button
            onClick={() => navigate(`/owner/complexes/${complex.id}`)}
            style={cancelButtonStyle}
            disabled={loading}
          >
            <X size={20} />
            Cancelar
          </button>
        </div>

        <div style={buttonGroupStyle}>
          <button
            onClick={(e) => handleSubmit(e, true)}
            style={saveAddButtonStyle}
            disabled={loading}
          >
            <Plus size={20} />
            Guardar y Agregar Otra
          </button>

          <button
            onClick={(e) => handleSubmit(e, false)}
            style={saveButtonStyle}
            disabled={loading}
          >
            <Save size={20} />
            {loading ? 'Guardando...' : 'Guardar Cancha'}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddFieldPage;
